/**
 * The workspace's data mode, visible to everyone in the workspace.
 *
 * A mode this server does not offer is rendered as a **locked row**: a lock
 * icon, the word "gesperrt", and a reason *specific to that mode*. It is never
 * a radio, never a disabled option inside the group of choices, never something
 * a click could reach — a mode the server refuses must not look like a setting
 * someone forgot to enable.
 *
 * What the locked row must not be is a dead end. Until 2026-08-20 `live` was
 * permanently locked and its row said so in a sentence about a CH/EU host that
 * is no longer the only route. Now the row says what the mode *means* — real
 * personal data — and on what conditions this server would offer it; and where
 * it IS offered, the selectable row says what actually happens to the files,
 * following `WorkspaceRef.us_transfer` rather than guessing from the mode name.
 *
 * Which modes are selectable comes from the server (`data_mode_options`), so
 * unlocking `live` is a server configuration and this component follows without
 * a change.
 */
import { useState } from 'react'
import { Check, Lock } from 'lucide-react'

import { api } from '@/api/client'
import { ErrorNote } from '@/components/ErrorNote'
import { Icon } from '@/components/Icon'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  ALL_DATA_MODES,
  DATA_MODE_LABEL,
  DATA_MODE_LOCKED_REASON,
  dataModeHint,
  isSelectable,
} from '@/features/workspace/dataModes'
import { useI18n } from '@/i18n'
import type { DataMode } from '@/features/workspace/dataModes'
import type { WorkspaceRef } from '@/api/types'

export function DataModeBadge({ workspace }: { workspace: WorkspaceRef }) {
  const { t } = useI18n()
  const mode = workspace.data_mode as DataMode
  return (
    <Badge variant="outline" data-testid="data-mode">
      {t('workspace.mode.label')}: {t(DATA_MODE_LABEL[mode] ?? 'workspace.mode.unknown')}
    </Badge>
  )
}

export function DataModeCard({
  workspace,
  canChange,
  onChanged,
}: {
  workspace: WorkspaceRef
  canChange: boolean
  onChanged: () => void
}) {
  const { t } = useI18n()
  const [saving, setSaving] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const change = async (mode: string) => {
    setSaving(mode)
    setError(null)
    try {
      await api.setDataMode(workspace.id, mode)
      onChanged()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setSaving(null)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('workspace.mode.title')}</CardTitle>
        <CardDescription>{t('workspace.mode.lead')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <ul className="flex flex-col gap-2" data-testid="data-mode-list">
          {ALL_DATA_MODES.map((mode) => {
            const active = workspace.data_mode === mode
            const selectable = isSelectable(workspace, mode)
            const label = t(DATA_MODE_LABEL[mode])
            const hint = t(dataModeHint(workspace, mode))

            if (!selectable) {
              // No control of any kind — not even a disabled one.
              return (
                <li
                  key={mode}
                  data-mode={mode}
                  data-locked="true"
                  className="flex items-start gap-3 rounded-sm border border-dashed border-border-strong bg-surface-2 px-4 py-3"
                >
                  <Icon icon={Lock} className="mt-0.5 text-muted" />
                  <div className="flex flex-col gap-1">
                    <span className="text-[14px] font-medium text-muted">
                      {label} · {t('workspace.mode.locked')}
                    </span>
                    <span className="text-[12px] text-muted">
                      {t(DATA_MODE_LOCKED_REASON[mode])}
                    </span>
                  </div>
                </li>
              )
            }

            if (!canChange) {
              return (
                <li
                  key={mode}
                  data-mode={mode}
                  data-active={active}
                  className="flex items-start gap-3 rounded-sm border border-border px-4 py-3"
                >
                  <Icon
                    icon={Check}
                    className={active ? 'mt-0.5 text-accent' : 'mt-0.5 text-transparent'}
                  />
                  <div className="flex flex-col gap-1">
                    <span className="text-[14px] font-medium text-ink">
                      {label}
                      {active ? ` · ${t('workspace.mode.current')}` : ''}
                    </span>
                    <span className="text-[12px] text-muted">{hint}</span>
                  </div>
                </li>
              )
            }

            return (
              <li key={mode} data-mode={mode} data-active={active}>
                <label className="flex cursor-pointer items-start gap-3 rounded-sm border border-border px-4 py-3 hover:bg-surface">
                  <input
                    type="radio"
                    name="data-mode"
                    value={mode}
                    checked={active}
                    disabled={saving !== null}
                    onChange={() => void change(mode)}
                    className="mt-1 accent-accent"
                  />
                  <span className="flex flex-col gap-1">
                    <span className="text-[14px] font-medium text-ink">{label}</span>
                    <span className="text-[12px] text-muted">{hint}</span>
                  </span>
                </label>
              </li>
            )
          })}
        </ul>

        {error ? <ErrorNote message={error} /> : null}

        <p className="text-[12px] text-muted">
          {t('workspace.retention', { days: workspace.retention_days })}
        </p>
      </CardContent>
    </Card>
  )
}
